import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// MED-42 — the half of the broadcast a page cannot receive. A file is read
// while the upload request is still running, and the page it redirects to only
// subscribes once it has been drawn: a picture read in that gap is announced to
// nobody, and the card stays on its placeholder until somebody reloads. Action
// Cable replays nothing, so the page catches up instead — once the stream is
// listening, it asks for itself again if it was drawn with something unread.
//
// Sits on the `turbo-cable-stream-source` itself, which marks a confirmed
// subscription with `connected`. Every transition counts, not only the first:
// a cable that dropped and came back has lost broadcasts the same way.
// `pending` is the server's word on whether anything was unread when the page
// was drawn; a morph that brings new unread cards in — a drop onto the library
// redirects back to it — changes it while the stream stays connected.
//
// The page has to refresh by morphing (`turbo_refreshes_with`), or every catch
// up would replace the stream source, subscribe anew and start the next one.
export default class extends Controller {
  static values = { pending: Boolean }

  connect() {
    this.observer = new MutationObserver(() => this.catchUp())
    this.observer.observe(this.element, { attributes: true, attributeFilter: ["connected"] })
    this.element.addEventListener("turbo:before-morph-attribute", this.keepConnected)
    document.addEventListener("turbo:before-morph-element", this.keepMessages)
    document.addEventListener("turbo:morph", this.caughtUp)
  }

  disconnect() {
    this.observer.disconnect()
    this.element.removeEventListener("turbo:before-morph-attribute", this.keepConnected)
    document.removeEventListener("turbo:before-morph-element", this.keepMessages)
    document.removeEventListener("turbo:morph", this.caughtUp)
  }

  // Also the first look: Stimulus calls this once when the controller starts,
  // which covers a stream that was already connected by then.
  pendingValueChanged() {
    this.catchUp()
  }

  catchUp() {
    if (!this.pendingValue || !this.element.hasAttribute("connected")) return

    // Turbo drops a refresh while another visit is under way — and the morph
    // after a drop is exactly where `pending` turns true. Wait for it to land.
    if (document.documentElement.hasAttribute("aria-busy")) {
      document.addEventListener("turbo:load", () => this.catchUp(), { once: true })
      return
    }

    this.catchingUp = true
    Turbo.session.refresh(document.baseURI)
  }

  // The page an upload redirects to carries its flash — how many media came in
  // and why the rest were refused (MED-36) — and the second rendering has none.
  // Morphed over, the message would vanish a moment after it appeared.
  keepMessages = (event) => {
    if (this.catchingUp && event.target.matches(".messages")) event.preventDefault()
  }

  caughtUp = () => {
    this.catchingUp = false
  }

  // The server's markup never carries `connected`, so a morph would take it
  // off a subscription that is still live — and with it every later catch up.
  keepConnected = (event) => {
    if (event.target === this.element && event.detail.attributeName === "connected") event.preventDefault()
  }
}
